import * as React from "react";

import Avatar from "@editor/components/common/designSystem/Avatar";
import Input from "@editor/components/common/designSystem/Input";
import { SidebarWorkspacesSkeleton } from "@editor/components/dashboard/SkeletonLoaders";
import { WorkspaceCardSkeleton } from "@editor/components/editor/SkeletonLoaders";
import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import { useSubscriptionInfo } from "@editor/hooks/subscription";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { useModal } from "@editor/hooks/useModal";
import { getProjectName } from "@editor/utils/project-utils";
import { trpc } from "@editor/utils/trpc";

import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import Popover from "@replo/design-system/components/popover/Popover";
import { Tag } from "@replo/design-system/components/tag/Tag";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import { ChevronsUpDown, Plus, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";

type CardProps = {
  workspaceName: string;
  endEnhancer?: React.ReactNode;
  className?: string;
  disableConditionalStyles?: boolean;
  isEmptyWorkspaces?: boolean;
  isPopoverOpen?: boolean;
  shopName?: string;
};

const ShopSelector: React.FC = () => {
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [searchStringInputValue, searchString, setSearchString] =
    useDebouncedState("");
  const _logEvent = useLogAnalytics();
  const navigate = useNavigate();
  const { openModal } = useModal();
  const shopId = useCurrentProjectId();
  const { user } = useCurrentUser();
  const { data, isLoading } =
    trpc.project.listGroupedByWorkspaceForUser.useQuery(user?.id ?? skipToken);
  const workspaceId = useCurrentWorkspaceId();
  const currentWorkspaceName = React.useMemo(
    () =>
      data?.find((workspace) => workspace.workspaceId === workspaceId)
        ?.workspaceName,
    [workspaceId, data],
  );
  const currentShop = React.useMemo(
    () =>
      data
        ?.flatMap((workspace) => workspace.projects)
        ?.find((project) => project.id === shopId),
    [shopId, data],
  );
  const currentShopName = getProjectName(currentShop);

  const onShopClick = (
    shopId: string,
    _shopName: string | undefined | null,
  ) => {
    navigate(`/shops/${shopId}/site`);
    setIsPopoverOpen(false);
    /* TODO (Craig, 2025-05-07): Add event logging for shops */
    // logEvent("dashboard.shops.switch", {
    //   toShopName: shopName,
    //   toShopId: shopId,
    // });
  };

  const onPopoverOpenChange = (isOpen: boolean) => {
    setIsPopoverOpen(isOpen);
    setSearchString("");
  };

  const filteredData = React.useMemo(() => {
    if (!data) {
      return [];
    }
    if (!searchString.trim()) {
      return data;
    }
    const lowercasedSearchString = searchString.toLowerCase();
    return data
      .map((workspace) => {
        const filteredProjects = workspace.projects.filter((project) =>
          project.name?.toLowerCase().includes(lowercasedSearchString),
        );
        const workspaceNameMatches = workspace.workspaceName
          .toLowerCase()
          .includes(lowercasedSearchString);
        if (workspaceNameMatches || filteredProjects.length > 0) {
          return {
            ...workspace,
            projects:
              workspaceNameMatches && filteredProjects.length === 0
                ? workspace.projects
                : filteredProjects,
          };
        }
        return null;
      })
      .filter((workspace) => workspace !== null) as typeof data;
  }, [data, searchString]);

  return (
    <Popover.Root isOpen={isPopoverOpen} onOpenChange={onPopoverOpenChange}>
      <Popover.Content
        shouldPreventDefaultOnInteractOutside={false}
        side="bottom"
        sideOffset={4}
        hideCloseButton
        align="start"
        className="w-[200px] p-0"
      >
        <div className="flex flex-col">
          <div className="flex flex-col">
            <div className="border-b-0.5 border-border flex flex-col gap-2 p-3">
              <Input
                size="base"
                placeholder="Search"
                type="text"
                value={searchStringInputValue}
                onChange={(e) => setSearchString(e.target.value ?? "")}
                startEnhancer={<Search className="text-muted" size={16} />}
              />
            </div>
            <div
              className={twMerge(
                "flex max-h-44 flex-col gap-2 px-3 py-1.5",
                // NOTE (Sebas, 2024-12-09): We need to add the scrollbar classes only if there are more than
                // 5 workspaces, otherwise the paddings will not be correct.
                "styled-scrollbar overflow-y-scroll pr-0",
              )}
              aria-label="workspaces"
            >
              {!isLoading &&
                filteredData?.map((workspace) => (
                  <div
                    key={workspace.workspaceId}
                    className="flex flex-col gap-1.5"
                  >
                    <div className="header-h5 flex h-6 min-h-6 items-center pl-2">
                      {workspace.workspaceName}
                    </div>
                    {workspace.projects.map((project) => (
                      <MenuItem
                        key={project.id}
                        variant="default"
                        size="base"
                        selected={project.id === shopId}
                        onClick={() => onShopClick(project.id, project.name)}
                        startEnhancer={
                          <Avatar
                            name={project.name ?? workspace.workspaceName}
                            size="xs"
                            className="rounded-md"
                            imageClassName={twMerge(
                              "bg-info-soft text-primary text-[10px]",
                              project.id === shopId && "bg-white",
                            )}
                          />
                        }
                      >
                        {project.name ?? workspace.workspaceName}
                      </MenuItem>
                    ))}
                    <MenuItem
                      key={`new-shop-${workspace.workspaceId}`}
                      variant="default"
                      size="base"
                      onClick={() =>
                        openModal({
                          type: "createShopFlowModal",
                          props: {
                            workspaceId: workspace.workspaceId,
                          },
                        })
                      }
                      startEnhancer={
                        <div className="border-border-component bg-sheet flex h-5 w-5 items-center justify-center rounded-[5px] border border-dashed">
                          <Plus
                            size={12}
                            className="text-muted"
                            absoluteStrokeWidth
                            strokeWidth={1.25}
                          />
                        </div>
                      }
                    >
                      New Shop
                    </MenuItem>
                  </div>
                ))}
              {isLoading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <SidebarWorkspacesSkeleton key={i.toString()} />
                ))}
              {!isLoading &&
                filteredData?.length === 0 &&
                searchString.trim() !== "" && (
                  <div className="text-muted p-2 text-center text-sm">
                    No results found.
                  </div>
                )}
            </div>
          </div>
          <div
            className={twMerge("text-primary border-t-0.5 border-border flex")}
          >
            <button
              className="hover:bg-button-link-hover flex h-8 w-full items-center gap-2 rounded-sm px-1 py-1.5 pl-5 text-sm"
              onClick={() => openModal({ type: "addWorkspaceModal" })}
            >
              <div className="flex h-5 w-5 items-center justify-center">
                <Plus size={14} />
              </div>
              New Workspace
            </button>
          </div>
        </div>
      </Popover.Content>
      <Popover.Trigger className="w-full" aria-label="select workspace">
        {isLoading ? (
          <WorkspaceCardSkeleton />
        ) : (
          <Card
            workspaceName={currentWorkspaceName ?? ""}
            shopName={currentShopName}
            endEnhancer={<ChevronsUpDown size={16} className="text-default" />}
            isPopoverOpen={isPopoverOpen}
          />
        )}
      </Popover.Trigger>
    </Popover.Root>
  );
};

const Card: React.FC<CardProps> = ({
  workspaceName,
  shopName,
  endEnhancer,
  className,
  disableConditionalStyles,
  isPopoverOpen,
}) => {
  const { user } = useCurrentUser();
  const workspaceId = useCurrentWorkspaceId();
  const {
    subscriptionInfo: { tier },
    isLoading,
  } = useSubscriptionInfo(workspaceId ?? undefined);

  if (!user) {
    return null;
  }

  return (
    <div
      className={twMerge(
        "flex flex-row gap-2 rounded-md p-1",
        !disableConditionalStyles && "hover:bg-menu-item-hover cursor-pointer",
        isPopoverOpen && "bg-menu-item-hover",
        className,
      )}
    >
      <Avatar
        name={shopName}
        isSquare
        size="base"
        className="mt-auto mb-auto rounded-md"
        imageClassName="bg-info-soft text-primary border-0.5 border-menu-item-selected"
      />
      <div className="flex w-full flex-col text-left">
        <div className="text-default inline-grid w-full text-base font-semibold">
          <span className="truncate">{shopName}</span>
        </div>
        <div className="flex flex-wrap gap-1 items-center text-muted text-sm">
          <span className="truncate max-w-[125px] min-w-0">
            {workspaceName}
          </span>
          {!isLoading && (
            <Tag variant="success" size="sm">
              <span className="capitalize">{tier}</span>
            </Tag>
          )}
        </div>
      </div>
      <div className="shrink-0">{endEnhancer}</div>
    </div>
  );
};

export default ShopSelector;
